/**
 * Text segment inspector (W2-06; DESIGN.md §6.8, rpe-format F§9.3). A text segment runs from a keyframe's leave
 * string to the next keyframe's arrive string; Phira derives its animation from the two strings, so the mode is
 * shown as derived and switching it rewrites the strings (Core.lines.setTextSegmentMode):
 *  - Switch / Type on / Erase / Extend / Counter with per-mode fields (texts; counter from / to numbers with the
 *    decimals Phira shows);
 *  - guards: an overshooting curve on Type on / Erase crashes Phira (the command sets Linear), on Extend it is
 *    allowed with a warning; a counter needs the whole text numeric;
 *  - the split exactness note (splitting a non-switch text segment is approximate) and a preview strip computed
 *    with Phira's string tween.
 * `Edit...` opens DIALOG.textMode for typing the texts of any mode before applying it.
 */

import { useCallback } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { DIALOG } from '../../../app/dialogs/types';
import { Core } from '../../../core/commands/index';
import { Ease } from '../../../core/ease/index';
import type { Ease as EaseValue } from '../../../core/ease/types';
import type { ChartDoc, DocIndexes, Key, KeyId } from '../../../core/model/types';
import { useT } from '../../../i18n/useT';
import type { DocStore, Selection } from '../../../state/types';
import { Button, Field, NumberField, Section, Segmented, TextArea } from '../../../ui';
import { useDocValue } from '../hooks';
import { TEXT_MODE_DESC, TEXT_MODE_LABEL } from '../labels';
import { m } from '../inspector.messages';
import {
  counterDecimals,
  counterText,
  counterValue,
  fieldOf,
  sortedIds,
  TEXT_MODES,
  textGuard,
  textModeOf,
  type TextMode,
} from '../model';
import { Callout, Readout, Row, Title, useSectionBase, useStore, WithInfo } from '../parts';
import s from '../Inspector.module.css';
import { useEditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';

/** A text segment: start key, next key and the strings Phira tweens between. */
export interface TextSegment {
  readonly key: Key;
  readonly next: Key;
  /** Leave string of the start key. */
  readonly start: string;
  /** Arrive string of the next key. */
  readonly end: string;
  /** Leave string of the next key (shown from the next keyframe on). */
  readonly after: string;
  readonly mode: TextMode;
}

export function textSegmentOf(
  doc: Pick<ChartDoc, 'keys' | 'tracks'>,
  idx: Pick<DocIndexes, 'nextKey'>,
  id: KeyId,
): TextSegment | null {
  const key = doc.keys.get(id);
  if (key === undefined) return null;
  const track = doc.tracks.get(key.track);
  if (track?.owner.kind !== 'line' || track.owner.prop !== 'text') return null;
  const nextId = idx.nextKey(id);
  const next = nextId === undefined ? undefined : doc.keys.get(nextId);
  if (next === undefined) return null;
  const start = key.vOut as string;
  const end = next.vIn as string;
  return { key, next, start, end, after: next.vOut as string, mode: textModeOf(start, end, key.ease) };
}

/** Text segments the selection points at: selected segments, else a single selected keyframe's outgoing one. */
export function selectedTextSegments(sel: Selection, store: DocStore | null): KeyId[] {
  if (store === null) return [];
  const candidates = sel.segments.size > 0 ? sortedIds(sel.segments) : sel.keys.size === 1 ? sortedIds(sel.keys) : [];
  return candidates.filter((id) => textSegmentOf(store.doc, store.idx, id) !== null);
}

const PREVIEW_POINTS = [0, 0.25, 0.5, 0.75, 1] as const;

/** What Phira shows at progress p of a segment (p = 1: the next keyframe's leave string). */
export function textAt(start: string, end: string, after: string, ease: EaseValue, p: number): string {
  if (p >= 1) return after;
  return Ease.stringTween(start, end, Ease.modelY(ease, p));
}

/** Five-cell preview of a text segment (0 %, 25 %, 50 %, 75 %, 100 %). */
export function TextPreviewStrip(props: {
  readonly start: string;
  readonly end: string;
  readonly after: string;
  readonly ease: EaseValue;
}) {
  const t = useT();
  return (
    <div className={s.strip} aria-label={t(m.textPreview)} role="group" data-testid="text-preview">
      {PREVIEW_POINTS.map((p) => (
        <div key={p} className={s.stripCell}>
          <span>{t(m.textPreviewAt, { p: p * 100 })}</span>
          <span className={s.stripText}>{textAt(props.start, props.end, props.after, props.ease, p)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------------------------------------------

export function TextSegmentSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = selectedTextSegments(props.selection, store);
  if (store === null || ids.length === 0) return null;
  return <TextSegmentBody services={props.services} store={store} ids={ids} />;
}

function TextSegmentBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly KeyId[];
}) {
  const { services, store, ids } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const idsKey = ids.join(',');
  const selector = useCallback(
    (doc: ChartDoc, idx: DocIndexes) =>
      (idsKey === '' ? [] : idsKey.split(',').map(Number))
        .map((id) => textSegmentOf(doc, idx, id as KeyId))
        .filter((x): x is TextSegment => x !== null),
    [idsKey],
  );
  const segs = useDocValue(store, selector, ['keys'], sameSegs);
  const edit = useEditKit(services, store, `text:${idsKey}`);
  const badges = useFieldProblems(services, { keys: segs.flatMap((x) => [x.key.id, x.next.id]) });
  const first = segs[0];
  if (first === undefined) return null;
  const single = segs.length === 1 ? first : null;
  const mode = fieldOf(segs.map((x) => x.mode));
  const segIds = segs.map((x) => x.key.id);

  const apply = (segment: KeyId, next: TextMode, texts: { readonly from?: string; readonly to?: string }): void => {
    edit.commit(FIELD.segmentTextMode, { key: m.txTextMode }, (tx) => {
      Core.lines.setTextSegmentMode(tx, { segment, mode: next, ...texts });
    });
  };

  const guard = single === null ? null : textGuard(single.mode, single.key.ease);
  const counterFrom = single === null ? null : counterValue(single.start);
  const counterTo = single === null ? null : counterValue(single.end);

  return (
    <Section
      title={
        <Title
          main={t(m.textSegTitle)}
          detail={
            single === null
              ? t(m.segmentsTitle, { count: segs.length })
              : t(m.segmentSpan, { from: base.fmtBeat(single.key.t), to: base.fmtBeat(single.next.t) })
          }
        />
      }
      actions={
        single === null ? undefined : (
          <Button
            size="sm"
            variant="ghost"
            icon="PencilLine"
            onClick={() => {
              void services.ui.openDialog(DIALOG.textMode, { segment: single.key.id });
            }}
          >
            {t(m.editMode)}
          </Button>
        )
      }
    >
      <div className={s.fields}>
        <Row edit={edit} field={FIELD.segmentTextMode}>
          <Field
            label={t(m.textMode)}
            helpId={FIELD.segmentTextMode}
            badges={badges(FIELD.segmentTextMode)}
            info={{ text: { key: m.textModeInfo }, rpeTerm: 'textEvents' }}
          >
            <Segmented<TextMode>
              ariaLabel={t(m.textMode)}
              size="sm"
              value={mode === null || mode.kind === 'mixed' ? 'mixed' : mode.value}
              options={TEXT_MODES.map((md) => ({ value: md, label: t(TEXT_MODE_LABEL[md]) }))}
              onChange={(next) => {
                edit.once({ key: m.txTextMode }, (tx) => {
                  for (const id of segIds) Core.lines.setTextSegmentMode(tx, { segment: id, mode: next });
                });
              }}
            />
          </Field>
          {mode !== null && mode.kind === 'value' ? (
            <Readout>{t(TEXT_MODE_DESC[mode.value])}</Readout>
          ) : (
            <Readout>{t(m.modeMixed)}</Readout>
          )}
        </Row>

        {single !== null && single.mode === 'switch' && (
          <div className={s.pair}>
            <TextRow
              edit={edit}
              field={`${FIELD.segmentFrom}.text`}
              label={t(m.textFrom)}
              value={single.start}
              onCommit={(v) => {
                apply(single.key.id, 'switch', { from: v });
              }}
            />
            <TextRow
              edit={edit}
              field={`${FIELD.segmentTo}.text`}
              label={t(m.textThen)}
              value={single.after}
              onCommit={(v) => {
                apply(single.key.id, 'switch', { to: v });
              }}
            />
          </div>
        )}
        {single !== null && single.mode === 'typeOn' && (
          <TextRow
            edit={edit}
            field={`${FIELD.segmentTo}.text`}
            label={t(m.textTypes)}
            value={single.end}
            onCommit={(v) => {
              apply(single.key.id, 'typeOn', { to: v });
            }}
          />
        )}
        {single !== null && single.mode === 'erase' && (
          <TextRow
            edit={edit}
            field={`${FIELD.segmentFrom}.text`}
            label={t(m.textErases)}
            value={single.start}
            onCommit={(v) => {
              apply(single.key.id, 'erase', { from: v });
            }}
          />
        )}
        {single !== null && single.mode === 'extend' && (
          <div className={s.pair}>
            <TextRow
              edit={edit}
              field={`${FIELD.segmentFrom}.text`}
              label={t(m.textStart)}
              value={single.start}
              onCommit={(v) => {
                apply(single.key.id, 'extend', { from: v });
              }}
            />
            <TextRow
              edit={edit}
              field={`${FIELD.segmentTo}.text`}
              label={t(m.textEnd)}
              value={single.end}
              onCommit={(v) => {
                apply(single.key.id, 'extend', { to: v });
              }}
            />
          </div>
        )}
        {single !== null && single.mode === 'counter' && (
          <>
            <div className={s.pair}>
              {(['from', 'to'] as const).map((side) => {
                const v = side === 'from' ? counterFrom : counterTo;
                const field = `${side === 'from' ? FIELD.segmentFrom : FIELD.segmentTo}.counter`;
                return (
                  <Row key={side} edit={edit} field={field}>
                    <NumberField
                      label={t(side === 'from' ? m.counterFrom : m.counterTo)}
                      value={v === null ? { kind: 'mixed' } : { kind: 'value', value: v }}
                      step={1}
                      precision={3}
                      helpId={field}
                      onCommit={(nv) => {
                        if (nv.kind !== 'number') return;
                        const text = counterText(nv.value);
                        apply(single.key.id, 'counter', side === 'from' ? { from: text } : { to: text });
                      }}
                    />
                  </Row>
                );
              })}
            </div>
            {counterFrom !== null && counterTo !== null ? (
              <Readout>{t(counterDecimals(counterFrom, counterTo) === 0 ? m.counterInt : m.counterDec)}</Readout>
            ) : (
              <Callout tone="warning">{t(m.counterNotNumeric)}</Callout>
            )}
          </>
        )}

        {guard === 'crash' && (
          <Callout tone="danger" testId="text-guard">
            {t(m.guardCrashNow)}
          </Callout>
        )}
        {guard === 'overshootWarning' && (
          <Callout tone="warning" testId="text-guard">
            {t(m.guardWarn)}
          </Callout>
        )}
        {single !== null && (single.mode === 'typeOn' || single.mode === 'erase') && guard === null && (
          <Readout>{t(m.guardCrash)}</Readout>
        )}
        {single !== null && single.mode !== 'switch' && <Readout testId="text-split">{t(m.textSplitApprox)}</Readout>}
        {single !== null && (
          <WithInfo info={{ text: m.textModeInfo }}>
            <TextPreviewStrip start={single.start} end={single.end} after={single.after} ease={single.key.ease} />
          </WithInfo>
        )}
      </div>
    </Section>
  );
}

function TextRow(props: {
  readonly edit: ReturnType<typeof useEditKit>;
  readonly field: string;
  readonly label: string;
  readonly value: string;
  readonly onCommit: (v: string) => void;
}) {
  return (
    <Row edit={props.edit} field={props.field}>
      <TextArea label={props.label} value={props.value} rows={2} helpId={props.field} onCommit={props.onCommit} />
    </Row>
  );
}

function sameSegs(a: readonly TextSegment[], b: readonly TextSegment[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => x.key === (b[i] as TextSegment).key && x.next === (b[i] as TextSegment).next);
}

export const textSegmentSectionDef: InspectorSectionDef = {
  id: 'textSegment',
  order: 190,
  level: 'guided',
  appliesTo: (sel, services) => selectedTextSegments(sel, services.store).length > 0,
  component: TextSegmentSection,
};
